import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Calendar, Clock, Users, Trash2, Edit, Send, ChevronLeft, ChevronRight,
  Plus, X, MapPin, User
} from 'lucide-react';

export default function AdminShiftManagement() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required_role: 'caregiver',
    shift_date: '',
    start_time: '',
    end_time: '',
    location: '',
    max_employees: 1,
    client_id: ''
  });

  const getRoleLabel = (role) => {
    const roleMap = {
      'caregiver': 'CNA',
      'dsp': 'DSP',
      'lpn': 'LPN',
      'rn': 'RN',
      'admin': 'Admin'
    };
    return roleMap[role] || role;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getShiftDotColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-500';
      case 'draft': return 'bg-gray-400';
      case 'in_progress': return 'bg-blue-500';
      case 'completed': return 'bg-purple-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  useEffect(() => {
    fetchShifts();
    fetchClients();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await api.get('/shifts/admin/all');
      setShifts(response.data);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    // Compare using UTC to avoid timezone issues
    return d1.getUTCFullYear() === d2.getUTCFullYear() &&
           d1.getUTCMonth() === d2.getUTCMonth() &&
           d1.getUTCDate() === d2.getUTCDate();
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    const d = new Date(date);
    return today.getFullYear() === d.getFullYear() &&
           today.getMonth() === d.getMonth() &&
           today.getDate() === d.getDate();
  };

  const getShiftsForDate = (date) => {
    if (!date) return [];
    // Normalize both dates to YYYY-MM-DD format for comparison
    const targetDate = date.toISOString().split('T')[0];
    return shifts.filter(shift => {
      const shiftDate = new Date(shift.shift_date).toISOString().split('T')[0];
      return shiftDate === targetDate;
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const openDayDialog = (date) => {
    setSelectedDate(date);
    setDayDialogOpen(true);
  };

  const openCreateDialog = (date = null) => {
    setFormData({
      title: '',
      description: '',
      required_role: 'caregiver',
      shift_date: date ? date.toISOString().split('T')[0] : '',
      start_time: '',
      end_time: '',
      location: '',
      max_employees: 1,
      client_id: ''
    });
    setCreateDialogOpen(true);
    setDayDialogOpen(false);
  };

  const openEditDialog = (shift) => {
    setSelectedShift(shift);
    setFormData({
      title: shift.title,
      description: shift.description || '',
      required_role: shift.required_role,
      shift_date: shift.shift_date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      location: shift.location || '',
      max_employees: shift.max_employees,
      client_id: shift.client_id || ''
    });
    setEditDialogOpen(true);
    setDayDialogOpen(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.shift_date || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataObj.append(key, formData[key]);
        }
      });

      await api.post('/shifts', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Shift created successfully');
      setCreateDialogOpen(false);
      fetchShifts();
    } catch (error) {
      toast.error('Failed to create shift');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataObj.append(key, formData[key]);
        }
      });

      await api.put(`/shifts/${selectedShift.id}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Shift updated successfully');
      setEditDialogOpen(false);
      fetchShifts();
    } catch (error) {
      toast.error('Failed to update shift');
    }
  };

  const handleDelete = async (shiftId) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success('Shift deleted');
      fetchShifts();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const handlePublish = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/publish`);
      toast.success('Shift published successfully');
      fetchShifts();
    } catch (error) {
      toast.error('Failed to publish shift');
    }
  };

  const handleRemoveEmployee = async (shiftId, assignmentId) => {
    try {
      await api.delete(`/shifts/${shiftId}/assignments/${assignmentId}`);
      toast.success('Employee removed from shift');
      fetchShifts();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Shift form component
  const ShiftForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Shift Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Morning Care Shift"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="shift_date">Date *</Label>
          <Input
            id="shift_date"
            type="date"
            value={formData.shift_date}
            onChange={(e) => setFormData({ ...formData, shift_date: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="required_role">Required Role *</Label>
          <select
            id="required_role"
            value={formData.required_role}
            onChange={(e) => setFormData({ ...formData, required_role: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="caregiver">CNA</option>
            <option value="dsp">DSP</option>
            <option value="lpn">LPN</option>
            <option value="rn">RN</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_time">Start Time *</Label>
          <Input
            id="start_time"
            type="time"
            value={formData.start_time}
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="end_time">End Time *</Label>
          <Input
            id="end_time"
            type="time"
            value={formData.end_time}
            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
            required
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="e.g., Client's Home - 123 Main St"
        />
      </div>

      <div>
        <Label htmlFor="client_id">Assign to Client (for GPS Verification)</Label>
        <select
          id="client_id"
          value={formData.client_id}
          onChange={(e) => {
            const clientId = e.target.value;
            setFormData({ ...formData, client_id: clientId });
            // Auto-fill location if client selected
            if (clientId) {
              const client = clients.find(c => c.id === clientId);
              if (client && client.address) {
                const address = `${client.address}, ${client.city || ''}, ${client.state || ''} ${client.zip_code || ''}`.trim();
                setFormData(prev => ({ ...prev, client_id: clientId, location: address }));
              }
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">-- No client (skip GPS verification) --</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.first_name} {client.last_name}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Linking to a client enables GPS verification at their residence
        </p>
      </div>
      
      <div>
        <Label htmlFor="max_employees">Max Employees</Label>
        <Input
          id="max_employees"
          type="number"
          min="1"
          value={formData.max_employees}
          onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
        />
      </div>
      
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Additional details about this shift..."
          rows={3}
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}>
          Cancel
        </Button>
        <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900">Shift Management</h2>
          <p className="text-gray-600">Schedule and manage employee shifts</p>
        </div>
        <Button
          onClick={() => openCreateDialog()}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="create-shift-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Shift
        </Button>
      </div>

      {/* Calendar Navigation */}
      <Card className="p-4 border-purple-100">
        <div className="flex items-center justify-between mb-4">
          <Button variant="outline" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-xl font-semibold text-purple-900">{monthYear}</h3>
          <Button variant="outline" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Week day headers */}
          {weekDays.map(day => (
            <div key={day} className="text-center py-2 text-sm font-semibold text-purple-700 bg-purple-50 rounded">
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {days.map((date, index) => {
            const dayShifts = getShiftsForDate(date);
            const isCurrentDay = isToday(date);
            
            return (
              <div
                key={index}
                onClick={() => date && openDayDialog(date)}
                className={`
                  min-h-[100px] p-2 border rounded-lg cursor-pointer transition-all
                  ${date ? 'hover:bg-purple-50 hover:border-purple-300' : 'bg-gray-50'}
                  ${isCurrentDay ? 'border-purple-500 border-2 bg-purple-50' : 'border-gray-200'}
                `}
                data-testid={date ? `calendar-day-${date.getDate()}` : null}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-purple-700' : 'text-gray-700'}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayShifts.slice(0, 3).map(shift => (
                        <div
                          key={shift.id}
                          className={`text-xs p-1 rounded truncate ${getStatusColor(shift.status)}`}
                          title={`${shift.title} (${shift.start_time} - ${shift.end_time})`}
                        >
                          {shift.start_time.slice(0, 5)} {shift.title}
                        </div>
                      ))}
                      {dayShifts.length > 3 && (
                        <div className="text-xs text-purple-600 font-medium">
                          +{dayShifts.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span className="text-xs text-gray-600">Draft</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-gray-600">Published</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-xs text-gray-600">In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-xs text-gray-600">Completed</span>
          </div>
        </div>
      </Card>

      {/* Day Detail Dialog */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-900 flex items-center justify-between">
              <span>{formatDate(selectedDate)}</span>
              <Button
                onClick={() => openCreateDialog(selectedDate)}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Shift
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedDate && getShiftsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No shifts scheduled for this day</p>
                <Button
                  onClick={() => openCreateDialog(selectedDate)}
                  className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Create a Shift
                </Button>
              </div>
            ) : (
              selectedDate && getShiftsForDate(selectedDate).map(shift => (
                <Card key={shift.id} className="p-4 border-purple-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-purple-900">{shift.title}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(shift.status)}`}>
                          {shift.status}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {getRoleLabel(shift.required_role)}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-purple-500" />
                          {shift.start_time} - {shift.end_time}
                        </div>
                        {shift.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-purple-500" />
                            {shift.location}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-purple-500" />
                          {shift.assigned_count || 0} / {shift.max_employees}
                        </div>
                      </div>

                      {shift.description && (
                        <p className="text-sm text-gray-500 mt-2">{shift.description}</p>
                      )}

                      {shift.assignments && shift.assignments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 mb-2">Assigned:</p>
                          <div className="flex flex-wrap gap-2">
                            {shift.assignments.map(a => (
                              <span key={a.id} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs">
                                <User className="w-3 h-3" />
                                {a.employee_name}
                                <button 
                                  onClick={() => handleRemoveEmployee(shift.id, a.id)}
                                  className="ml-1 hover:text-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-1 ml-4">
                      {shift.status === 'draft' && (
                        <Button
                          onClick={() => handlePublish(shift.id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => openEditDialog(shift)}
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:bg-purple-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(shift.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Shift Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-purple-900">Create New Shift</DialogTitle>
          </DialogHeader>
          <ShiftForm onSubmit={handleCreate} submitLabel="Create Shift" />
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-purple-900">Edit Shift</DialogTitle>
          </DialogHeader>
          <ShiftForm onSubmit={handleUpdate} submitLabel="Update Shift" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
